import { MdCheckCircle, MdErrorOutline, MdVisibilityOff } from "react-icons/md";
import type { GrammarFormPublicationState } from "./grammarFormPublicationState";

interface GrammarFormPublicationBadgeProps {
  state: GrammarFormPublicationState;
  /** Compact rendering for tight spaces like a list-view row — same icon/label/aria, less padding/chrome. */
  compact?: boolean;
}

const STATE_CONFIG = {
  blocked_draft: {
    label: "Draft — content has errors",
    Icon: MdErrorOutline,
    bgColor: "bg-error-600/90",
  },
  ready_draft: {
    label: "Draft — ready to publish",
    Icon: MdErrorOutline,
    bgColor: "bg-amber-500/90",
  },
  hidden: {
    label: "Hidden",
    Icon: MdVisibilityOff,
    bgColor: "bg-muted/80",
  },
  published: {
    label: "Published",
    Icon: MdCheckCircle,
    bgColor: "bg-success-600/90",
  },
} as const;

/**
 * Shared badge for grammar-form publication state — used by both the slide card (grid + list view) and
 * the editor. Every state pairs an icon with a text label so the distinction never relies on color alone.
 */
export function GrammarFormPublicationBadge({
  state,
  compact = false,
}: GrammarFormPublicationBadgeProps) {
  if (state === "not_applicable") return null;

  const { label, Icon, bgColor } = STATE_CONFIG[state];

  return (
    <div
      role="status"
      aria-label={label}
      className={`flex items-center gap-1.5 text-white rounded-full ${bgColor} ${
        compact ? "px-2 py-0.5" : "px-2.5 py-1 backdrop-blur-sm shadow-md"
      }`}
    >
      <Icon className="w-3.5 h-3.5" />
      <span className="text-xs font-medium">{label}</span>
    </div>
  );
}
